/** "What happened?": the verification and every step the agent really took for one answer. */
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare, faBolt, faCircleCheck, faCircleQuestion, faCircleXmark, faClock, faFileLines, faLink, faMinus,
  faShieldHalved, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { formatSecs } from './studioApi';
import { Badge, Collapsible, EffectIcon, JsonView, Spinner } from './ui';

export function VerdictBadge({ v, verifying }) {
  const { t } = useTranslation();
  if (verifying) return <span className="as-verdict"><Spinner size={10} /> {t('agent_studio_verifying')}</span>;
  if (!v) return null;
  if (v.verdict === 'ok') {
    return <span className="as-verdict is-ok" title={v.summary}><FontAwesomeIcon icon={faShieldHalved} /> {t('agent_studio_verdict_ok')}</span>;
  }
  if (v.verdict === 'warning') {
    return <span className="as-verdict is-warn" title={v.summary}><FontAwesomeIcon icon={faTriangleExclamation} /> {t('agent_studio_verdict_warning')}</span>;
  }
  if (v.verdict === 'problem') {
    return <span className="as-verdict is-bad" title={v.summary}><FontAwesomeIcon icon={faCircleXmark} /> {t('agent_studio_verdict_problem')}</span>;
  }
  return <span className="as-verdict">{t('agent_studio_verdict_none')}</span>;
}

export function effectLabel(t, effect) {
  return t(`agent_studio_effect_${effect || 'lookup'}`);
}

function stepTitle(t, s) {
  switch (s.type) {
    case 'tool': return `${effectLabel(t, s.effect)}: ${s.name}`;
    case 'platform': return t(`agent_studio_platform_${s.name}`, { defaultValue: s.label || s.name });
    case 'route': return t('agent_studio_step_route');
    case 'knowledge': return t('agent_studio_step_knowledge');
    case 'skills_loaded': return t('agent_studio_step_skills_loaded');
    case 'handoff': return t('agent_studio_step_handoff');
    default: return s.type;
  }
}

function StepStatus({ s }) {
  const { t } = useTranslation();
  if (s.type === 'route' || s.type === 'knowledge' || s.type === 'skills_loaded') return null;
  if (s.blocked) return <Badge tone="amber">{t('agent_studio_step_blocked')}</Badge>;
  if (s.ok === false) return <Badge tone="red">{t('agent_studio_step_failed')}</Badge>;
  if (s.output && typeof s.output === 'object' && s.output.test) return <Badge tone="blue">{t('agent_studio_step_test_only')}</Badge>;
  return <Badge tone="green">{t('agent_studio_step_ok')}</Badge>;
}

/** A knowledge source: a web page opens in a new tab (shown without https:// and www.), a document or
 *  Q&A is its name. */
function Source({ src }) {
  const text = String(src);
  if (!/^https?:\/\//i.test(text)) {
    return <span className="as-source"><FontAwesomeIcon icon={faFileLines} className="as-source-ic" /><span>{text}</span></span>;
  }
  return (
    <a className="as-source is-link" href={text} target="_blank" rel="noopener noreferrer" title={text}>
      <FontAwesomeIcon icon={faLink} className="as-source-ic" />
      <span>{text.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}</span>
      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="as-source-out" />
    </a>
  );
}

// the skills a route step picked: next to its title, in the same row as the icon
const SKILL_STEPS = ['route', 'skills_loaded'];
function StepSkills({ s }) {
  const { t } = useTranslation();
  const skills = s.skills || [];
  if (!skills.length) return <span className="as-small as-muted">{t('agent_studio_step_no_skill')}</span>;
  return skills.map((k) => <Badge key={k.id} tone="brand">{k.name}</Badge>);
}

function StepBody({ s }) {
  const { t } = useTranslation();
  if (s.type === 'knowledge') {
    return (
      <>
        <span className="as-small">{t('agent_studio_step_knowledge_found', { found: s.found ?? 0, checked: s.checked ?? 0 })}</span>
        {s.sources?.length > 0 && (
          <ul className="as-sources">
            {s.sources.map((src) => <li key={src}><Source src={src} /></li>)}
          </ul>
        )}
      </>
    );
  }
  return s.summary ? <span className="as-small">{s.summary}</span> : null;
}

function Step({ s, lang }) {
  const { t } = useTranslation();
  const icon = s.type === 'tool' || s.type === 'platform' ? s.effect : s.type;
  const bad = (s.type === 'tool' || s.type === 'platform') && (s.ok === false || s.blocked);
  return (
    <li className="as-step" title={s.type === 'route' ? s.reason || undefined : undefined}>
      <span className={`as-step-icon ${bad ? 'is-bad' : s.type === 'route' ? 'is-route' : ''}`}><EffectIcon effect={icon} /></span>
      <div className="as-step-main">
        <div className="as-row-wrap as-step-head">
          <strong>{stepTitle(t, s)}</strong>
          {SKILL_STEPS.includes(s.type) ? <StepSkills s={s} /> : <StepStatus s={s} />}
          {s.ms > 0 && <span className="as-small as-muted">{formatSecs(s.ms, lang)}</span>}
        </div>
        {!SKILL_STEPS.includes(s.type) && <StepBody s={s} />}
        {(s.type === 'tool' || s.type === 'platform') && (s.error || s.input || s.output) && (
          <Collapsible title={<span className="as-small as-muted">{t('agent_studio_step_data')}</span>}>
            {s.error && <div className="as-error">{s.error}</div>}
            {s.input && Object.keys(s.input).length > 0 && (
              <><div className="as-small as-muted">{t('agent_studio_step_input')}</div><JsonView value={s.input} /></>
            )}
            {s.output != null && <><div className="as-small as-muted">{t('agent_studio_step_output')}</div><JsonView value={s.output} /></>}
          </Collapsible>
        )}
      </div>
    </li>
  );
}

const CLAIM_ICON = {
  supported: <FontAwesomeIcon icon={faCircleCheck} className="as-ok" />,
  unsupported: <FontAwesomeIcon icon={faCircleQuestion} className="as-warn" />,
  contradicted: <FontAwesomeIcon icon={faCircleXmark} className="as-bad" />,
  generic: <FontAwesomeIcon icon={faMinus} className="as-muted" />,
};

function VerificationCard({ v, verifying }) {
  const { t } = useTranslation();
  if (verifying) return <div className="as-verify is-pending"><Spinner size={14} /> {t('agent_studio_verifying_long')}</div>;
  if (!v) return null;
  const tone = { ok: 'is-ok', warning: 'is-warn', problem: 'is-bad' }[v.verdict] || '';
  return (
    <div className={`as-verify ${tone}`}>
      <div className="as-verify-head">
        <FontAwesomeIcon icon={v.verdict === 'ok' ? faShieldHalved : v.verdict === 'problem' ? faCircleXmark : faTriangleExclamation} />
        {t(`agent_studio_verify_head_${v.verdict}`, { defaultValue: t('agent_studio_verdict_none') })}
      </div>
      <p>{v.summary}</p>
      {v.claims?.length > 0 && (
        <Collapsible defaultOpen={v.verdict !== 'ok'}
          title={<span className="as-small">{t('agent_studio_verify_claims', { n: v.claims.length })}</span>}>
          <ul className="as-claims">
            {v.claims.map((c, i) => (
              <li key={i}>
                {CLAIM_ICON[c.status] || CLAIM_ICON.generic}
                <div><div>{c.claim}</div>{c.evidence && <div className="as-small as-muted">{c.evidence}</div>}</div>
              </li>
            ))}
          </ul>
        </Collapsible>
      )}
      {v.behavior_issues?.length > 0 && (
        <div className="as-warn-text">
          <div className="as-small"><strong>{t('agent_studio_verify_behavior')}</strong></div>
          <ul>{v.behavior_issues.map((b, i) => <li key={i}>{b}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/** An answer that was not checked automatically (the agent's setting is off): the owner checks it here. */
function CheckOffer({ onCheck }) {
  const { t } = useTranslation();
  return (
    <div className="as-verify is-unchecked">
      <span className="as-verify-ask"><FontAwesomeIcon icon={faShieldHalved} /> {t('agent_studio_check_unchecked')}</span>
      <button type="button" className="as-btn as-btn-ghost as-btn-sm" onClick={onCheck}
        title={t('agent_studio_check_answer_hint')}>
        {t('agent_studio_check_answer')}
      </button>
    </div>
  );
}

export default function TraceView({ trace, verifying, live, onCheck }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  if (!trace) return <div className="as-trace-empty">{t('agent_studio_trace_empty')}</div>;
  if (trace.paused) {
    return <div className="as-trace-empty">{t(trace.paused === 'live_agent' ? 'agent_studio_handoff_waiting' : 'agent_studio_trace_paused')}</div>;
  }
  const steps = trace.steps || [];
  const actions = steps.filter((s) => s.type !== 'route');
  const m = trace.metrics;
  const offerCheck = !live && !verifying && !trace.verification && trace.checkable && onCheck;
  return (
    <div className="as-trace">
      {offerCheck ? <CheckOffer onCheck={onCheck} /> : <VerificationCard v={trace.verification} verifying={verifying} />}
      <div>
        <div className="as-eyebrow">{t('agent_studio_trace_title')} {live && <Spinner size={10} />}</div>
        <ol className="as-steps">{steps.map((s, i) => <Step key={i} s={s} lang={lang} />)}</ol>
        {trace.verbatim && <div className="as-info">{t('agent_studio_trace_verbatim')}</div>}
        {!live && actions.length === 0 && <div className="as-info">{t('agent_studio_trace_no_action')}</div>}
      </div>
      {trace.error && <div className="as-error">{t('agent_studio_trace_error', { error: trace.error })}</div>}
      {m && (
        <div className="as-metrics">
          <Metric icon={faBolt} label={t('agent_studio_metric_first')} value={formatSecs(m.first_token_ms, lang)} />
          <Metric icon={faClock} label={t('agent_studio_metric_total')} value={formatSecs(m.total_ms, lang)} />
        </div>
      )}
    </div>
  );
}

/** One timing: label above, the number large with a small unit ("5.1" + "s"). */
function Metric({ icon, label, value }) {
  const cut = value.lastIndexOf(' ');
  const [num, unit] = cut > 0 ? [value.slice(0, cut), value.slice(cut + 1)] : [value, ''];
  return (
    <div className="as-metric">
      <div className="as-metric-label"><FontAwesomeIcon icon={icon} /> {label}</div>
      <div className="as-metric-value">{num}{unit && <span className="as-metric-unit">{unit}</span>}</div>
    </div>
  );
}
